import React from 'react';
import i18next from 'i18next';
import dayjs from 'dayjs';

import './WheelContainer.scss';

import { Wheel } from '../../Wheel/Wheel';
import { DropdownBox } from '../../DropdownBox/DropdownBox';
import { ManageCredits } from '../../ManageCredits/ManageCredits';
import { ProductTypes } from '../../../constants/ProductTypes';
import { Close } from '@material-ui/icons';
import { mediumYellow, lightGrey } from '@signaturit/react-components-library';
import { WheelColor } from './WheelColor.enum';
import { brandPrimary, dangerColor } from '@signaturit/react-components-library/src/style/commons/_colors';

interface SGWheelContainerProps {
    id: string;
    type: ProductTypes;
    totCredits: number;
    usedCredits: number;
    period: string;
    periodEndingDate: string;
    forceClickToHide?: boolean;
    onCloseModal?: (type: ProductTypes) => void;
}

export const mediumOrange = '#FF981C';

export class WheelContainer extends React.Component<SGWheelContainerProps, any> {
    private DASHARRAY = 113;
    private COLOR_WHEEL = {
        full: brandPrimary,
        semiFull: mediumYellow,
        semiEmpty: mediumOrange,
        empty: dangerColor,
        hidden: lightGrey,
    };
    private COLOR_BACKGROUND_WHEEL = {
        full: '#f5f5ff',
        empty: '#fbc7ce'
    }
    private SHOW_DROPDOWN = 'dropdown-box__container--show';
    private HIDE_DROPDOWN = 'dropdown-box__container--hide';
    private autoHide = true;

    constructor(props: SGWheelContainerProps) {
        super(props);

        this.autoHide = !props.forceClickToHide;

        this.state = {
            offset: this.DASHARRAY,
            color: this.COLOR_WHEEL.full,
            show: 'block',
            remainingCredits: 0,
            showDropDown: props.forceClickToHide,
        };
    }

    componentDidMount() {
        this.defineWheelColor();
        this.setState({ offset: this.calculateBarOffset() });
        this.setState({ remainingCredits: this.calculateRemainingCredits() });
    }

    componentDidUpdate({ forceClickToHide: prevForceClickToHide }: SGWheelContainerProps) {
        const { forceClickToHide } = this.props;
        const hasChangedForceClickToHide = prevForceClickToHide !== forceClickToHide;

        if (hasChangedForceClickToHide) {
            this.autoHide = !forceClickToHide;
            this.setState({ showDropDown: forceClickToHide });
        }
    }

    private calculatePercentage(): number {
        return (this.props.usedCredits / this.props.totCredits) * 100;
    }

    private calculateRemainingCredits(): number {
        return this.props.totCredits - this.props.usedCredits;
    }

    private defineFigureInComponent(): string {
        return this.state.remainingCredits > 999
            ? `${Math.floor((this.state.remainingCredits / 1000) * 10) / 10}k`
            : this.state.remainingCredits.toString();
    }

    private defineWheelColor(): void {
        const percentage = this.calculatePercentage();
        if (percentage >= 0 && percentage < 100) {
            this.setState({ color: this.COLOR_WHEEL.full });
            this.setState({ colorLabel: WheelColor.full });
            this.setState({ colorBackground: this.COLOR_BACKGROUND_WHEEL.full })
        } else {
            this.setState({ color: this.COLOR_WHEEL.empty });
            this.setState({ colorLabel: WheelColor.empty });
            this.setState({ colorBackground: this.COLOR_BACKGROUND_WHEEL.empty })
        }
    }

    private calculateBarOffset(): number {
        const percentage = this.calculatePercentage();
        if(percentage === 100 || percentage === 0){
            return this.DASHARRAY;
        }
        return Math.floor(this.DASHARRAY - 1 - percentage * (this.DASHARRAY / 100));
    }

    private mapGaugeType(): string {
        switch (this.props.type) {
            case ProductTypes.AdvancedSignature:
                return i18next.t('default.gauges.advanced_signature');
            case ProductTypes.Batch:
                return i18next.t('default.gauges.batch');
            case ProductTypes.CertifiedEmail:
                return i18next.t('default.gauges.certified_email');
            case ProductTypes.CertifiedFile:
                return i18next.t('default.gauges.certified_file');
            case ProductTypes.CertifiedSms:
                return i18next.t('default.gauges.certified_sms');
            case ProductTypes.PhotoId:
                return i18next.t('default.gauges.photo_id');
            case ProductTypes.QualifiedSignature:
                return i18next.t('default.gauges.qualified_signature');
            case ProductTypes.SimpleSignature:
                return i18next.t('default.gauges.simple_signature');
            case ProductTypes.SmartSignature:
                return i18next.t('default.gauges.smart_signature');
            case ProductTypes.SmsCredits:
                return i18next.t('default.gauges.sms_credits');
            default:
                return '';
        }
    }

    public showDropDown(): void {
        if (this.autoHide) {
            this.setState({ showDropDown: true });
        }
    }
    public hideDropDown(): void {
        if (this.autoHide) {
            this.setState({ showDropDown: false });
        }
    }

    public enableAutoHide(): void {
        const { onCloseModal } = this.props;

        this.autoHide = true;
        this.hideDropDown();

        if (onCloseModal) {
            onCloseModal(this.props.type);
        }
    }

    private formatDate(date: string): string {
        const dateToFormat = dayjs(date);
        return dateToFormat.format('DD/MM/YYYY');
    }

    render() {
        const { type, id, period, periodEndingDate, totCredits, usedCredits } = this.props;
        const { show, offset, color, colorLabel, colorBackground, showDropDown } = this.state;
        return (
            <div
                className="wheel-container__holder"
                onMouseOver={() => this.showDropDown()}
                onMouseOut={() => this.hideDropDown()}
            >
                <Wheel
                    show={show}
                    offset={offset}
                    wheelColor={color}
                    wheelColorLabel={colorLabel}
                    wheelColorBackground={colorBackground}
                    remainingCredits={this.defineFigureInComponent()}
                    dasharray={this.DASHARRAY}
                    id={id}
                />

                <div className={`wheel-container__popup-container`}>
                    <DropdownBox showBox={showDropDown ? this.SHOW_DROPDOWN : this.HIDE_DROPDOWN} color={colorLabel}>
                        {!this.autoHide && (
                            <div className="wheel-container__popup-close-button" onClick={() => this.enableAutoHide()}>
                                <Close fontSize={'inherit'} />
                            </div>
                        )}

                        <div className={`wheel-container__popup wheel-container__popup--${colorLabel}`}>
                            <span
                                className={`wheel-container__popup-available wheel-container__popup-available--${colorLabel}`}
                            >
                                {i18next.t('default.gauges._popup.message_available', {
                                    credits: this.calculateRemainingCredits(),
                                })}
                            </span>
                            <span className="wheel-container__popup-used">
                                {i18next.t('default.gauges._popup.message_used', {
                                    credits: usedCredits,
                                })}
                            </span>
                            <span className="wheel-container__popup-contracted">
                                {i18next.t('default.gauges._popup.message_contracted', {
                                    credits: totCredits,
                                })}
                            </span>
                            <ManageCredits type={type} colorClassLabel={colorLabel} />
                            <span className="wheel-container__popup-next-renewal">
                                {period &&
                                    periodEndingDate &&
                                    i18next.t('default.gauges._popup.message_next_renewal', {
                                        date: this.formatDate(periodEndingDate),
                                    })}
                            </span>
                        </div>
                    </DropdownBox>
                </div>

                <p>{this.mapGaugeType()}</p>
            </div>
        );
    }
}
